<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFlag } from '@loadsmart/vue-feature-flags';
import { SMS_COUNTRIES, SystemMessages } from '@nova/core';

const { t } = useI18n();
const checkInSmsTemplateFlag = useFlag('enable-check-in-sms-template');

type GateManagementCheckInSmsSettingsModel = {
  gateManagementCustomFirstSmsMessage: string;
  gateManagementIncludeYardMapInFirstSms: boolean;
};

const {
  entitySettings,
  warehouse,
  isGateManagementEnabled,
  readOnly = false
} = defineProps<{
  entitySettings: GateManagementCheckInSmsSettingsModel;
  warehouse: Record<string, any>;
  isGateManagementEnabled: boolean;
  readOnly?: boolean;
}>();

const localSettings = computed(() => entitySettings);

const isSmsWarehouse = computed(() => SMS_COUNTRIES.includes(warehouse?.country ?? 'USA'));
const hasYardMap = computed(() => Boolean(warehouse?.settings?.yardMap));

const defaultMessage = computed(() =>
  SystemMessages.INFO.DRIVER_INTERACTIVE_GREETING(warehouse?.name ?? '', warehouse?.country)
);

const displayMessage = computed(
  () => localSettings.value.gateManagementCustomFirstSmsMessage || defaultMessage.value
);

const isEditing = ref(false);
const draftMessage = ref('');
const showEmptyError = ref(false);

function startEditing() {
  draftMessage.value = displayMessage.value;
  showEmptyError.value = false;
  isEditing.value = true;
}

function cancelEditing() {
  isEditing.value = false;
  showEmptyError.value = false;
}

function resetToDefault() {
  localSettings.value.gateManagementCustomFirstSmsMessage = '';
  isEditing.value = false;
  showEmptyError.value = false;
}

function saveMessage() {
  const trimmed = draftMessage.value.trim();
  if (!trimmed) {
    showEmptyError.value = true;
    return;
  }
  localSettings.value.gateManagementCustomFirstSmsMessage = trimmed;
  isEditing.value = false;
  showEmptyError.value = false;
}

watch(
  hasYardMap,
  hasMap => {
    if (!hasMap) {
      localSettings.value.gateManagementIncludeYardMapInFirstSms = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-if="checkInSmsTemplateFlag?.enabled && isGateManagementEnabled && isSmsWarehouse"
    class="d-flex flex-column my-4">
    <div>
      <p class="typography-body-sm-bold d-flex align-center mb-2">
        {{ t('warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.label') }}
        <help-icon-tooltip icon-style-class="grey" class="ml-1">
          {{ t('warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.tooltip') }}
        </help-icon-tooltip>
      </p>

      <div v-if="!isEditing" class="d-flex align-start justify-space-between gap-2">
        <p class="typography-body-md flex-grow-1" data-testid="check-in-sms-message-preview">
          {{ displayMessage }}
        </p>
        <icon-tooltip-button
          v-if="!readOnly"
          edit
          :tooltip="t('global.buttons.edit')"
          size="small"
          data-testid="edit-check-in-sms-message"
          @click="startEditing" />
      </div>

      <div v-else class="d-flex flex-column gap-2">
        <v-textarea
          v-model="draftMessage"
          data-testid="gate-management-custom-first-sms-message"
          variant="outlined"
          auto-grow
          rows="2"
          hide-details="auto"
          :error-messages="
            showEmptyError
              ? [
                  t(
                    'warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.cannotBeEmpty'
                  )
                ]
              : []
          "
          @update:model-value="showEmptyError = false" />
        <div class="d-flex justify-end gap-2">
          <v-btn variant="outlined" data-testid="cancel-check-in-sms-edit" @click="cancelEditing">
            {{ t('global.buttons.nevermind') }}
          </v-btn>
          <v-btn
            variant="outlined"
            data-testid="reset-check-in-sms-default"
            @click="resetToDefault">
            {{ t('global.buttons.resetToDefault') }}
          </v-btn>
          <v-btn color="primary" data-testid="save-check-in-sms-edit" @click="saveMessage">
            {{ t('global.buttons.save') }}
          </v-btn>
        </div>
      </div>
    </div>

    <v-divider class="my-4" />

    <div>
      <div class="gm-switch-row d-flex align-center pa-1 gap-3">
        <v-switch
          v-model="localSettings.gateManagementIncludeYardMapInFirstSms"
          color="primary"
          hide-details
          :disabled="readOnly || !hasYardMap"
          data-testid="gate-management-include-yard-map-switch" />
        <span class="typography-body-md">
          {{ t('warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.shareYardMap') }}
        </span>
      </div>
      <p class="typography-body-sm mt-1">
        {{
          hasYardMap
            ? t(
                'warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.shareYardMapDescription'
              )
            : t(
                'warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.yardMapRequiredNote'
              )
        }}
        <router-link
          v-if="hasYardMap && warehouse?.id"
          class="ml-1"
          data-testid="check-in-sms-see-map-link"
          :to="{
            name: 'warehouses.details.general',
            params: { warehouseId: warehouse.id },
            hash: '#yard-map-section'
          }">
          {{ t('warehouse.pages.settings.expansionPanels.gateManagement.checkInSms.seeMap') }}
        </router-link>
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.gm-switch-row :deep(.v-switch__track) {
  height: 14px;
  min-width: 36px;
  border-radius: 7px;
  opacity: 1;
}

.gm-switch-row :deep(.v-switch__thumb) {
  width: 20px;
  height: 20px;
}

.gm-switch-row :deep(.v-selection-control--checked .v-switch__track) {
  background-color: $color-primary-60;
}

.gm-switch-row :deep(.v-selection-control--checked .v-switch__thumb) {
  background-color: $color-primary-100;
}

.gm-switch-row :deep(:not(.v-selection-control--checked) .v-switch__track) {
  background-color: $color-neutral-40;
}

.gm-switch-row :deep(:not(.v-selection-control--checked) .v-switch__thumb) {
  background-color: $color-background-primary;
}
</style>
